import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { JettyMap } from '../../../core/bookingParameters';

import {
  CountryISO,
  NgxIntlTelInputModule,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { CruiseManagerService } from '../../../services/resources/cruise-manager.service';
import { CruiseManagerInterface } from '../../../core/models';
import { first } from 'rxjs';

@Component({
  selector: 'app-infodetails',
  imports: [CommonModule, ReactiveFormsModule, NgxIntlTelInputModule],
  standalone: true,
  template: `
    <div [formGroup]="cruiseDetails" class="flex flex-col gap-y-4 py-5 w-full">
      <div class="flex flex-col gap-y-4 w-full">
        <div class="w-full">
          <label for="combobox" class="block text-sm font-medium text-gray-700"
            >Cruisemanager</label
          >
          <div class="relative mt-1">
            <input
              id="combobox"
              type="text"
              formControlName="cruiseManager"
              class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              role="combobox"
              aria-controls="options"
              aria-expanded="false"
            />
            <button
              type="button"
              (click)="toggleCruiseManager()"
              class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
            >
              <svg
                class="h-5 w-5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>

            <ul
              *ngIf="showDropdown"
              class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              id="options"
              role="listbox"
            >
              <li
                *ngFor="let cruiseManager of cruiseManagers"
                (click)="select(cruiseManager)"
                class="hover:text-white hover:bg-columbus-blue-600 active:bg-columbus-blue-700 active:text-white relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900"
                id="option-0"
                role="option"
                tabindex="-1"
              >
                <!-- Selected: "font-semibold" -->
                <span class="block truncate">{{ cruiseManager.name }}</span>
              </li>

              <!-- More items... -->
            </ul>
          </div>
        </div>
        <div class="w-full">
          <label
            for="cruiseManagerTel"
            class="block text-sm font-medium leading-6 text-gray-700 mb-2"
            >Cruisemanager Tel</label
          >
          <ngx-intl-tel-input
            [cssClass]="
              'mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6'
            "
            [preferredCountries]="preferredCountries"
            [enableAutoCountrySelect]="true"
            [enablePlaceholder]="true"
            [searchCountryFlag]="true"
            [searchCountryField]="[
              SearchCountryField.Iso2,
              SearchCountryField.Name,
            ]"
            [selectFirstCountry]="false"
            [selectedCountryISO]="countryCode"
            [phoneValidation]="true"
            [separateDialCode]="true"
            formControlName="cruiseManagerTel"
          ></ngx-intl-tel-input>
          <div
            *ngIf="
              cruiseDetails.get('cruiseManagerTel')?.invalid &&
              cruiseDetails.get('cruiseManagerTel')?.touched
            "
            class="text-red-500 text-sm mt-1"
          >
            Valid phone number is required.
          </div>
        </div>
        <div class="flex flex-col w-full">
          <label
            for="jettyDropdown"
            class="mb-2 text-sm font-medium text-gray-700"
            >Anlegestelle</label
          >
          <select
            id="jettyDropdown"
            formControlName="jetty"
            class="text-xs w-full rounded-md border border-gray-300 py-2 px-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option *ngFor="let key of jettyKeys" [value]="key">
              {{ jettyMap[key] }}
            </option>
          </select>
        </div>

        <div class="flex flex-row gap-x-3 sm:gap-x-6">
          <!-- Pax am Schiff Input -->
          <div class="flex flex-col w-full">
            <label for="paxShip" class="mb-2 text-sm font-medium text-gray-700">
              Pax am Schiff
            </label>
            <input
              id="paxShip"
              type="number"
              formControlName="paxShip"
              class="text-xs w-full rounded-md border border-gray-300 py-2 px-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Pax am Schiff eingeben..."
            />
          </div>

          <!-- Pax on Tour Input -->
          <div class="flex flex-col w-full">
            <label for="paxTour" class="mb-2 text-sm font-medium text-gray-700">
              Pax on Tour
            </label>
            <input
              id="paxTour"
              type="number"
              formControlName="paxTour"
              class="text-xs w-full rounded-md border border-gray-300 py-2 px-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Pax on Tour eingeben..."
            />
          </div>
        </div>
        <!-- internNote -->
        <div class="flex flex-col w-full">
          <label
            for="internNote"
            class="mb-2 text-sm font-medium text-gray-700"
          >
            Interne Notiz
          </label>
          <input
            id="internNote"
            type="text"
            formControlName="internNote"
            class="text-xs w-full rounded-md border border-gray-300 py-2 px-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <!-- Rechnungsnummer Input -->
        <div class="flex flex-col">
          <label
            for="invoiceNumber"
            class="mb-2 text-sm font-medium text-gray-700"
          >
            Rechnungsnummer
          </label>
          <input
            id="invoiceNumber"
            type="number"
            formControlName="invoiceNumber"
            class="text-xs w-full rounded-md border border-gray-300 py-2 px-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Rechnungsnummer eingeben..."
          />
        </div>
      </div>
    </div>
  `,
  styles: ``,
})
export class InfodetailsComponent implements OnInit {
  @Input({ required: true }) cruiseDetails!: FormGroup;

  cruiseManagerServcie = inject(CruiseManagerService);

  cruiseManagers$ = this.cruiseManagerServcie.getCruiseManagers();

  jettyMap = JettyMap;
  jettyKeys = Object.keys(JettyMap);
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [
    CountryISO.Austria,
    CountryISO.Germany,
    CountryISO.Switzerland,
    CountryISO.Hungary,
    CountryISO.Slovakia,
  ];
  countryCode: CountryISO = CountryISO.Austria;
  PhoneNumberFormat = PhoneNumberFormat;

  selectedCruiseManager: CruiseManagerInterface | undefined = undefined;
  cruiseManagers: CruiseManagerInterface[] = [];

  showDropdown = false;

  toggleCruiseManager() {
    this.showDropdown = !this.showDropdown;
  }

  select(value: CruiseManagerInterface) {
    this.cruiseDetails.get('cruiseManager')?.setValue(value.name);
    this.patchTel(value.tel);
    this.selectedCruiseManager = value;
    this.toggleCruiseManager();
  }

  ngOnInit(): void {
    const cruiseManagerTel = this.cruiseDetails
      .get('cruiseManagerTel')
      ?.getRawValue();
    if (cruiseManagerTel) {
      this.patchTel(cruiseManagerTel);
    }

    this.cruiseManagers$.pipe(first()).subscribe((cruiseManagers) => {
      if (cruiseManagers) {
        this.cruiseManagers = [...cruiseManagers].sort((a, b) =>
          a.name.localeCompare(b.name, 'de'),
        );
      }
    });
  }

  patchTel(tel: string) {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parse(tel, 'ZZ');
    const countryC = phoneUtil.getRegionCodeForNumber(number) as CountryISO;
    const numToPatch = number.getNationalNumber()?.toString() || '';
    this.cruiseDetails.get('cruiseManagerTel')?.setValue(numToPatch);
    this.countryCode = countryC;
  }
}
